import React, {useMemo} from "react"
import {KlassappFilters, FilterField} from "../../uiKit/KlassappFilters/KlassappFilters"
import {AppliedFilter, FieldType, FilterKey} from "types/filter"
import ProgramSelect from "components/ProgramSelect"
import {useTranslation} from "react-i18next"
import CampusSelect from "components/CampusSelect"
import {useAllActivities, useAllDepartments, useModel} from "hooks"
import {KlassDropdown} from "components/Select"
import {pick} from "lodash"
import UserSelect from "components/UserSelect"
import {Auth} from "types/auth"
import {BaseButton} from "components/buttons"
import {Icon} from "components/Icon"

export type StudentActivityFiltersProps = {
    isCompact?: boolean
    filterKey?: FilterKey
    initialFilter?: any
    onApply: (payload: AppliedFilter) => void
    onClear?: () => void
    className?: string
    departmentId?: number
    onAddClick?: () => void
}

function StudentActivityFilters({
    isCompact = false,
    filterKey = FilterKey.StudentActivity,
    initialFilter,
    onApply,
    onClear,
    className,
    departmentId,
    onAddClick
}: StudentActivityFiltersProps) {
    const {t} = useTranslation(["activities", "common"])
    const model = useModel()
    const {departments} = useAllDepartments()
    const {activities: allActivities} = useAllActivities()
    const activities = useMemo(() => {
        let departmentIdToUserActivitiesFrom: number | null = null

        if (departmentId) {
            const moduleDepartment = departments.find((dept) => dept.departmentId === departmentId)

            departmentIdToUserActivitiesFrom = moduleDepartment?.useActivitiesFromDepartmentId
        }
        return allActivities
            .filter((activity) => {
                if (!activity.isActive) return false

                if (departmentIdToUserActivitiesFrom && activity.departmentId !== departmentIdToUserActivitiesFrom)
                    return false

                return true
            })
            .map((activity) => pick(activity, ["activityId", "name"]))
    }, [departments, allActivities, departmentId])

    // Define availableFilters based on INITIAL_FILTER structure
    const availableFilters: FilterField[] = useMemo(
        () => [
            {
                field: "activityDate",
                label: "Activity Dates",
                type: FieldType.Date
            },
            {
                field: "departments",
                label: t("activities.department"),
                type: FieldType.List,
                renderForm: (props: any) => {
                    return (
                        <KlassDropdown
                            isDisabled={!!departmentId}
                            isMulti
                            options={model.departments}
                            labelKey="name"
                            valueKey="departmentId"
                            placeholder={t("activities.department")}
                            {...props}
                        />
                    )
                }
            },
            {
                field: "activities",
                label: t("activities.activityType"),
                type: FieldType.List,
                renderForm: (props: any) => {
                    return (
                        <KlassDropdown
                            isMulti
                            options={activities}
                            labelKey="name"
                            valueKey="activityId"
                            placeholder={t("activities.activityType")}
                            {...props}
                        />
                    )
                }
            },
            {
                field: "campuses",
                label: t("activities.campus"),
                type: FieldType.List,
                renderForm: (props: any) => {
                    return <CampusSelect isMulti placeholder={t("activities.campus")} {...props} />
                }
            },
            {
                field: "programs",
                label: t("activities.program"),
                type: FieldType.List,
                renderForm: (props: any) => {
                    return <ProgramSelect isMulti placeholder={t("activities.program")} {...props} />
                }
            },
            {
                field: "assignedProfiles",
                label: t("activities.assignedProfile"),
                type: FieldType.List,
                renderForm: (props: any) => {
                    return (
                        <UserSelect
                            type={Auth.UserProfileType.Staff}
                            isMulti
                            isClearable
                            placeholder={t("activities.assignedProfile")}
                            {...props}
                        />
                    )
                }
            },
            {
                field: "completedBy",
                label: t("activities.completedBy"),
                type: FieldType.List,
                renderForm: (props: any) => {
                    return (
                        <UserSelect
                            type={Auth.UserProfileType.Staff}
                            isMulti
                            isClearable
                            placeholder={t("activities.completedBy")}
                            {...props}
                        />
                    )
                }
            },
            {
                field: "completedDate",
                label: "Completion Date",
                type: FieldType.Date
            },
            {
                field: "studentStartDate",
                label: "Student Start Date",
                type: FieldType.Date
            },
            {
                field: "studentDueDate",
                label: "Due Date",
                type: FieldType.Date
            },
            {
                field: "mineOnly",
                label: "My Activities Only",
                type: FieldType.Flag
            }
        ],
        [t, departmentId, activities, model.departments]
    )

    return (
        <KlassappFilters
            filterKey={filterKey}
            availableFilters={availableFilters}
            initialFilter={initialFilter}
            onApply={onApply}
            onClear={onClear}
            className={className}
            isCompact={isCompact}
            renderAdditionalFilter={() => (
                <div className="flex flex-row justify-end items-center">
                    {!!onAddClick && (
                        <BaseButton
                            title={t("common:applicationProgress.add")}
                            icon={<Icon icon="PLUS" color="#FFF" />}
                            onClick={onAddClick}
                        />
                    )}
                </div>
            )}
        />
    )
}

export default StudentActivityFilters
